import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LogOut, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TechLogin } from "@/components/tech/TechLogin";
import { JobCard } from "@/components/tech/JobCard";

export default function TechPortal() {
  const [params, setParams] = useSearchParams();
  const [tech, setTech] = useState(null); // null checking · false signed-out · object signed-in
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const booted = useRef(false); // StrictMode double-mount guard — the magic-link token is single-use

  const loadJobs = () => { setLoading(true); return api.get("/tech/jobs").then((r) => { setTech(r.data.tech); setJobs(r.data.jobs); return true; }).catch(() => { setTech(false); return false; }).finally(() => setLoading(false)); };

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    const token = params.get("token");
    if (token) {
      let failMsg = "";
      api.post("/tech/verify", { token }).then(() => toast.success("Signed in — welcome"))
        .catch((e) => { failMsg = e?.response?.data?.detail || "That link didn't work — request a new one"; })
        .finally(async () => { setParams({}, { replace: true }); const ok = await loadJobs(); if (!ok && failMsg) toast.error(failMsg); }); // a used link is fine if this device already has a session
    } else loadJobs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => { await api.post("/tech/logout").catch(() => {}); setTech(false); setJobs([]); };
  const replace = (j) => setJobs((all) => all.map((x) => (x.job_id === j.job_id ? j : x)));

  if (tech === null) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (tech === false) return <TechLogin />;

  const mine = jobs.filter((j) => j.mine);
  const open = jobs.filter((j) => !j.mine && !j.taken);
  const past = jobs.filter((j) => !j.mine && j.taken);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="tech-portal">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="dg-eyebrow">Job board</div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1" data-testid="tech-portal-title">Hi {tech.name.split(" ")[0]}</h1>
          <p className="text-slate-400 text-sm">Raise your hand on any open job — David confirms who gets it.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={loadJobs} className="dg-btn-ghost rounded-full p-2" title="Refresh" data-testid="tech-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button type="button" onClick={logout} className="dg-btn-ghost rounded-full px-3 py-2 text-sm inline-flex items-center gap-1" data-testid="tech-logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>

      {mine.length > 0 && <section className="mb-8" data-testid="tech-my-jobs"><h2 className="text-base md:text-lg font-semibold text-emerald-300 mb-3">Your jobs</h2><div className="space-y-4">{mine.map((j) => <JobCard key={j.job_id} job={j} onChange={replace} />)}</div></section>}
      <section data-testid="tech-open-jobs">
        <h2 className="text-base md:text-lg font-semibold mb-3">Open jobs <span className="text-slate-500 text-sm">({open.length})</span></h2>
        {open.length === 0 ? <p className="text-slate-500 text-sm" data-testid="tech-no-open">Nothing open right now — you'll get an email when David posts one.</p>
          : <div className="space-y-4">{open.map((j) => <JobCard key={j.job_id} job={j} onChange={replace} />)}</div>}
      </section>
      {past.length > 0 && <section className="mt-8 opacity-70" data-testid="tech-past-jobs"><h2 className="text-base md:text-lg font-semibold mb-3 text-slate-400">Taken by others</h2><div className="space-y-4">{past.map((j) => <JobCard key={j.job_id} job={j} onChange={replace} />)}</div></section>}
    </div>
  );
}
